import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=user
  const [error, setError] = useState("");
  const [pendingLogin, setPendingLogin] = useState(null); // { pending_id, masked_phone, expires_in, demo_code?, mock_mode }

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password, remember = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      // Sevkiyat / skip_2fa yolu: backend cookie'yi anında set eder, `user`
      // objesi ile birlikte döner.  OTP UI adımını atlayıp doğrudan giriş.
      if (data?.direct_login && data?.user) {
        setUser(data.user);
        setPendingLogin(null);
        return { direct: true, data };
      }
      // Step 1 success → pending OTP
      setPendingLogin(data);
      return { pending: true, data };
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return { pending: false, error: true };
    }
  };

  const loginWithPin = async (username, pin, remember = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login-pin", { username, pin, remember });
      if (data?.direct_login && data?.user) {
        setUser(data.user);
        setPendingLogin(null);
        return { direct: true, data };
      }
      return { error: true };
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 423 → detail is a dict with `code=pin_locked` and Turkish message
      if (e.response?.status === 423 && detail && typeof detail === "object" && detail.code === "pin_locked") {
        setError(detail.message);
        return { locked: true, lockedUntil: detail.locked_until, message: detail.message };
      }
      setError(formatApiErrorDetail(detail) || e.message);
      return { error: true };
    }
  };

  const verifyOtp = async (code) => {
    setError("");
    if (!pendingLogin?.pending_id) {
      setError("Doğrulama isteği yok, yeniden giriş yapın");
      return false;
    }
    try {
      const { data } = await api.post("/auth/verify-otp", {
        pending_id: pendingLogin.pending_id,
        code,
      });
      setUser(data);
      setPendingLogin(null);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const resendOtp = async () => {
    setError("");
    if (!pendingLogin?.pending_id) return null;
    try {
      const { data } = await api.post("/auth/resend-otp", { pending_id: pendingLogin.pending_id });
      // Merge new expires + optional demo code
      setPendingLogin({ ...pendingLogin, ...data });
      return data;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return null;
    }
  };

  const cancelOtp = () => setPendingLogin(null);

  const register = async (name, email, password, phone) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password, phone });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
      setPendingLogin(null);
      // Purge cached per-session state so the next login starts clean
      try {
        localStorage.removeItem("nf_company");
      } catch {}
    }
  };

  const isAdmin = user && user !== false && user.role === "admin";
  const isMuhasebe = user && user !== false && user.role === "muhasebe";
  const isViewer = user && user !== false && user.role === "viewer";
  const isSales = user && user !== false && user.role === "satis";
  const isKademe = user && user !== false && user.role === "kademe";
  const isSantiye = user && user !== false && user.role === "santiye";
  const isSirketMuduru = user && user !== false && user.role === "sirket_muduru";
  const isBayi = user && user !== false && user.role === "bayi";
  const isSevkiyat = user && user !== false && user.role === "sevkiyat";
  const isOperator = user && user !== false && user.role === "operator";
  // A user can write (create/edit) contracts/records if not viewer.
  // Satış Personeli can only write to contracts — handled per-page.
  const canWrite = user && user !== false && user.role !== "viewer";
  // Production write permission: Admin + Şantiye Müdürü only.
  const canWriteProduction = !!(isAdmin || isSantiye);
  // Dealer order approver: Admin + Şantiye + Şirket Müdürü.
  const canApproveDealerOrders = !!(isAdmin || isSantiye || isSirketMuduru);

  return (
    <AuthContext.Provider value={{
      user, error, setError, pendingLogin,
      login, loginWithPin, verifyOtp, resendOtp, cancelOtp,
      register, logout,
      isAdmin, isMuhasebe, isViewer, isSales, isKademe, isSantiye, isSirketMuduru, isBayi, isSevkiyat, isOperator,
      canWrite, canWriteProduction, canApproveDealerOrders,
      refresh: bootstrap,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
